// Theme Engine - Multiple Dark & Fruity Themes
import { getPreferences, updatePreferenceDirect } from '../hooks/usePreferences';

export interface Theme {
  id: string;
  name: string;
  description: string;
  colors: {
    // Backgrounds
    bgPrimary: string;
    bgSecondary: string;
    bgTertiary: string;

    // Text
    textPrimary: string;
    textSecondary: string;
    textMuted: string;

    // Accents
    accent: string;
    accentHover: string;

    // Status colors
    success: string;
    error: string;
    warning: string;
    info: string;

    // Chart colors
    chartBackground: string;
    chartText: string;
    chartGrid: string;
    chartBorder: string;
    candleUp: string;
    candleDown: string;

    // Indicators
    ema9: string;
    ema21: string;
    ema200: string;
    vwap: string;

    // Borders & Dividers
    border: string;
    borderLight: string;
  };
}

export const themes: Record<string, Theme> = {
  // Original Default Theme
  midnight: {
    id: 'midnight',
    name: 'Midnight Blue',
    description: 'Original dark blue theme',
    colors: {
      bgPrimary: '#0a0e27',
      bgSecondary: '#1a1f3a',
      bgTertiary: '#2a3150',

      textPrimary: '#ffffff',
      textSecondary: '#d1d4dc',
      textMuted: '#8892b0',

      accent: '#4a9eff',
      accentHover: '#5fadff',

      success: '#26a69a',
      error: '#ef5350',
      warning: '#ff8c42',
      info: '#4fc3f7',

      chartBackground: '#0a0e27',
      chartText: '#d1d4dc',
      chartGrid: 'rgba(42, 49, 80, 0.5)',
      chartBorder: '#2a3150',
      candleUp: '#26a69a',
      candleDown: '#ef5350',

      ema9: '#f50057',
      ema21: '#ab47bc',
      ema200: '#ffc107',
      vwap: '#4fc3f7',

      border: '#2a3150',
      borderLight: '#3a4160',
    },
  },

  // Berry Dark Theme
  berry: {
    id: 'berry',
    name: 'Berry Blast',
    description: 'Dark with vibrant berry colors',
    colors: {
      bgPrimary: '#0d0a1a',
      bgSecondary: '#1a1229',
      bgTertiary: '#2a1f3d',

      textPrimary: '#ffffff',
      textSecondary: '#e0d4f0',
      textMuted: '#9b8bb3',

      accent: '#d946ef',
      accentHover: '#e879f9',

      success: '#22c55e',
      error: '#ef4444',
      warning: '#f59e0b',
      info: '#a855f7',

      chartBackground: '#0d0a1a',
      chartText: '#e0d4f0',
      chartGrid: 'rgba(168, 85, 247, 0.15)',
      chartBorder: '#2a1f3d',
      candleUp: '#22c55e',
      candleDown: '#ef4444',

      ema9: '#ec4899',
      ema21: '#d946ef',
      ema200: '#f59e0b',
      vwap: '#a855f7',

      border: '#2a1f3d',
      borderLight: '#3d2f52',
    },
  },

  // Ocean Theme
  ocean: {
    id: 'ocean',
    name: 'Deep Ocean',
    description: 'Dark blue-teal ocean theme',
    colors: {
      bgPrimary: '#061726',
      bgSecondary: '#0a2540',
      bgTertiary: '#14344f',

      textPrimary: '#ffffff',
      textSecondary: '#d4e9f7',
      textMuted: '#7ea8c9',

      accent: '#06b6d4',
      accentHover: '#22d3ee',

      success: '#10b981',
      error: '#f43f5e',
      warning: '#f59e0b',
      info: '#14b8a6',

      chartBackground: '#061726',
      chartText: '#d4e9f7',
      chartGrid: 'rgba(6, 182, 212, 0.15)',
      chartBorder: '#14344f',
      candleUp: '#10b981',
      candleDown: '#f43f5e',

      ema9: '#22d3ee',
      ema21: '#0ea5e9',
      ema200: '#f59e0b',
      vwap: '#14b8a6',

      border: '#14344f',
      borderLight: '#1e4a6b',
    },
  },

  // Sunset Theme
  sunset: {
    id: 'sunset',
    name: 'Neon Sunset',
    description: 'Dark with warm sunset colors',
    colors: {
      bgPrimary: '#1a0f0f',
      bgSecondary: '#2d1818',
      bgTertiary: '#3d2424',

      textPrimary: '#ffffff',
      textSecondary: '#ffd4d4',
      textMuted: '#c99999',

      accent: '#f97316',
      accentHover: '#fb923c',

      success: '#84cc16',
      error: '#dc2626',
      warning: '#facc15',
      info: '#fb923c',

      chartBackground: '#1a0f0f',
      chartText: '#ffd4d4',
      chartGrid: 'rgba(249, 115, 22, 0.15)',
      chartBorder: '#3d2424',
      candleUp: '#84cc16',
      candleDown: '#dc2626',

      ema9: '#f97316',
      ema21: '#ea580c',
      ema200: '#facc15',
      vwap: '#fb923c',

      border: '#3d2424',
      borderLight: '#4d3030',
    },
  },

  // Lime Theme
  lime: {
    id: 'lime',
    name: 'Electric Lime',
    description: 'Dark with electric lime accents',
    colors: {
      bgPrimary: '#0f1a0f',
      bgSecondary: '#1a2b1a',
      bgTertiary: '#243d24',

      textPrimary: '#ffffff',
      textSecondary: '#d4f7d4',
      textMuted: '#99c999',

      accent: '#84cc16',
      accentHover: '#a3e635',

      success: '#22c55e',
      error: '#f43f5e',
      warning: '#eab308',
      info: '#06b6d4',

      chartBackground: '#0f1a0f',
      chartText: '#d4f7d4',
      chartGrid: 'rgba(132, 204, 22, 0.15)',
      chartBorder: '#243d24',
      candleUp: '#22c55e',
      candleDown: '#f43f5e',

      ema9: '#84cc16',
      ema21: '#65a30d',
      ema200: '#eab308',
      vwap: '#06b6d4',

      border: '#243d24',
      borderLight: '#2f4d2f',
    },
  },

  // Grape Theme
  grape: {
    id: 'grape',
    name: 'Midnight Grape',
    description: 'Dark purple grape theme',
    colors: {
      bgPrimary: '#12091f',
      bgSecondary: '#1f1433',
      bgTertiary: '#2d1f47',

      textPrimary: '#ffffff',
      textSecondary: '#e4d9f7',
      textMuted: '#a399c9',

      accent: '#8b5cf6',
      accentHover: '#a78bfa',

      success: '#10b981',
      error: '#f43f5e',
      warning: '#f59e0b',
      info: '#8b5cf6',

      chartBackground: '#12091f',
      chartText: '#e4d9f7',
      chartGrid: 'rgba(139, 92, 246, 0.15)',
      chartBorder: '#2d1f47',
      candleUp: '#10b981',
      candleDown: '#f43f5e',

      ema9: '#c084fc',
      ema21: '#8b5cf6',
      ema200: '#f59e0b',
      vwap: '#a78bfa',

      border: '#2d1f47',
      borderLight: '#3d2b5c',
    },
  },

  // Dracula - Classic popular dark theme
  dracula: {
    id: 'dracula',
    name: 'Dracula',
    description: 'Classic purple-accented dark theme',
    colors: {
      bgPrimary: '#282a36',
      bgSecondary: '#343746',
      bgTertiary: '#44475a',

      textPrimary: '#f8f8f2',
      textSecondary: '#e0e0e0',
      textMuted: '#6272a4',

      accent: '#bd93f9',
      accentHover: '#caa9fa',

      success: '#50fa7b',
      error: '#ff5555',
      warning: '#f1fa8c',
      info: '#8be9fd',

      chartBackground: '#282a36',
      chartText: '#f8f8f2',
      chartGrid: 'rgba(98, 114, 164, 0.3)',
      chartBorder: '#44475a',
      candleUp: '#50fa7b',
      candleDown: '#ff5555',

      ema9: '#ff79c6',
      ema21: '#bd93f9',
      ema200: '#f1fa8c',
      vwap: '#8be9fd',

      border: '#44475a',
      borderLight: '#5a5e73',
    },
  },

  // Nord - Arctic bluish theme
  nord: {
    id: 'nord',
    name: 'Nord',
    description: 'Arctic, bluish-grey color palette',
    colors: {
      bgPrimary: '#2e3440',
      bgSecondary: '#3b4252',
      bgTertiary: '#434c5e',

      textPrimary: '#eceff4',
      textSecondary: '#e5e9f0',
      textMuted: '#7b88a1',

      accent: '#88c0d0',
      accentHover: '#8fbcbb',

      success: '#a3be8c',
      error: '#bf616a',
      warning: '#ebcb8b',
      info: '#81a1c1',

      chartBackground: '#2e3440',
      chartText: '#eceff4',
      chartGrid: 'rgba(76, 86, 106, 0.5)',
      chartBorder: '#434c5e',
      candleUp: '#a3be8c',
      candleDown: '#bf616a',

      ema9: '#b48ead',
      ema21: '#81a1c1',
      ema200: '#ebcb8b',
      vwap: '#88c0d0',

      border: '#434c5e',
      borderLight: '#4c566a',
    },
  },

  // One Dark - Atom inspired
  onedark: {
    id: 'onedark',
    name: 'One Dark',
    description: 'Atom editor inspired theme',
    colors: {
      bgPrimary: '#21252b',
      bgSecondary: '#282c34',
      bgTertiary: '#2c323c',

      textPrimary: '#abb2bf',
      textSecondary: '#9da5b4',
      textMuted: '#5c6370',

      accent: '#61afef',
      accentHover: '#74b9f0',

      success: '#98c379',
      error: '#e06c75',
      warning: '#e5c07b',
      info: '#56b6c2',

      chartBackground: '#21252b',
      chartText: '#abb2bf',
      chartGrid: 'rgba(92, 99, 112, 0.3)',
      chartBorder: '#2c323c',
      candleUp: '#98c379',
      candleDown: '#e06c75',

      ema9: '#c678dd',
      ema21: '#61afef',
      ema200: '#e5c07b',
      vwap: '#56b6c2',

      border: '#3e4451',
      borderLight: '#4b5363',
    },
  },

  // Monokai - Sublime Text classic
  monokai: {
    id: 'monokai',
    name: 'Monokai',
    description: 'Sublime Text classic theme',
    colors: {
      bgPrimary: '#272822',
      bgSecondary: '#2d2e27',
      bgTertiary: '#383930',

      textPrimary: '#f8f8f2',
      textSecondary: '#e0e0dc',
      textMuted: '#75715e',

      accent: '#a6e22e',
      accentHover: '#b8f344',

      success: '#a6e22e',
      error: '#f92672',
      warning: '#e6db74',
      info: '#66d9ef',

      chartBackground: '#272822',
      chartText: '#f8f8f2',
      chartGrid: 'rgba(117, 113, 94, 0.3)',
      chartBorder: '#383930',
      candleUp: '#a6e22e',
      candleDown: '#f92672',

      ema9: '#fd971f',
      ema21: '#ae81ff',
      ema200: '#e6db74',
      vwap: '#66d9ef',

      border: '#383930',
      borderLight: '#49483e',
    },
  },

  // Tokyo Night
  tokyonight: {
    id: 'tokyonight',
    name: 'Tokyo Night',
    description: 'Clean dark theme with neon accents',
    colors: {
      bgPrimary: '#1a1b26',
      bgSecondary: '#24283b',
      bgTertiary: '#2f3549',

      textPrimary: '#c0caf5',
      textSecondary: '#a9b1d6',
      textMuted: '#565f89',

      accent: '#7aa2f7',
      accentHover: '#89b4fa',

      success: '#9ece6a',
      error: '#f7768e',
      warning: '#e0af68',
      info: '#7dcfff',

      chartBackground: '#1a1b26',
      chartText: '#c0caf5',
      chartGrid: 'rgba(86, 95, 137, 0.3)',
      chartBorder: '#2f3549',
      candleUp: '#9ece6a',
      candleDown: '#f7768e',

      ema9: '#bb9af7',
      ema21: '#7aa2f7',
      ema200: '#e0af68',
      vwap: '#7dcfff',

      border: '#2f3549',
      borderLight: '#3b4261',
    },
  },

  // Catppuccin Mocha
  catppuccin: {
    id: 'catppuccin',
    name: 'Catppuccin',
    description: 'Soothing pastel theme',
    colors: {
      bgPrimary: '#1e1e2e',
      bgSecondary: '#2a2a3c',
      bgTertiary: '#313244',

      textPrimary: '#cdd6f4',
      textSecondary: '#bac2de',
      textMuted: '#6c7086',

      accent: '#cba6f7',
      accentHover: '#d4b8fa',

      success: '#a6e3a1',
      error: '#f38ba8',
      warning: '#f9e2af',
      info: '#89dceb',

      chartBackground: '#1e1e2e',
      chartText: '#cdd6f4',
      chartGrid: 'rgba(108, 112, 134, 0.3)',
      chartBorder: '#313244',
      candleUp: '#a6e3a1',
      candleDown: '#f38ba8',

      ema9: '#f5c2e7',
      ema21: '#cba6f7',
      ema200: '#f9e2af',
      vwap: '#89dceb',

      border: '#313244',
      borderLight: '#45475a',
    },
  },

  // Gruvbox Dark
  gruvbox: {
    id: 'gruvbox',
    name: 'Gruvbox',
    description: 'Retro groovy warm colors',
    colors: {
      bgPrimary: '#282828',
      bgSecondary: '#3c3836',
      bgTertiary: '#504945',

      textPrimary: '#ebdbb2',
      textSecondary: '#d5c4a1',
      textMuted: '#928374',

      accent: '#fe8019',
      accentHover: '#fabd2f',

      success: '#b8bb26',
      error: '#fb4934',
      warning: '#fabd2f',
      info: '#83a598',

      chartBackground: '#282828',
      chartText: '#ebdbb2',
      chartGrid: 'rgba(146, 131, 116, 0.3)',
      chartBorder: '#504945',
      candleUp: '#b8bb26',
      candleDown: '#fb4934',

      ema9: '#d3869b',
      ema21: '#fe8019',
      ema200: '#fabd2f',
      vwap: '#83a598',

      border: '#504945',
      borderLight: '#665c54',
    },
  },

  // Glassmorphic Frost
  glassfrost: {
    id: 'glassfrost',
    name: 'Glass Frost',
    description: 'Frosted glass with soft blues',
    colors: {
      bgPrimary: 'rgba(15, 23, 42, 0.95)',
      bgSecondary: 'rgba(30, 41, 59, 0.85)',
      bgTertiary: 'rgba(51, 65, 85, 0.75)',

      textPrimary: '#f1f5f9',
      textSecondary: '#cbd5e1',
      textMuted: '#64748b',

      accent: '#38bdf8',
      accentHover: '#7dd3fc',

      success: '#4ade80',
      error: '#fb7185',
      warning: '#fbbf24',
      info: '#38bdf8',

      chartBackground: 'rgba(15, 23, 42, 0.9)',
      chartText: '#f1f5f9',
      chartGrid: 'rgba(100, 116, 139, 0.2)',
      chartBorder: 'rgba(51, 65, 85, 0.5)',
      candleUp: '#4ade80',
      candleDown: '#fb7185',

      ema9: '#f472b6',
      ema21: '#38bdf8',
      ema200: '#fbbf24',
      vwap: '#22d3ee',

      border: 'rgba(71, 85, 105, 0.5)',
      borderLight: 'rgba(100, 116, 139, 0.4)',
    },
  },

  // Glassmorphic Aurora
  aurora: {
    id: 'aurora',
    name: 'Aurora Glass',
    description: 'Northern lights glassmorphism',
    colors: {
      bgPrimary: 'rgba(10, 10, 30, 0.95)',
      bgSecondary: 'rgba(20, 20, 50, 0.85)',
      bgTertiary: 'rgba(35, 35, 70, 0.75)',

      textPrimary: '#e0f2fe',
      textSecondary: '#bae6fd',
      textMuted: '#7dd3fc',

      accent: '#22d3ee',
      accentHover: '#67e8f9',

      success: '#34d399',
      error: '#fb7185',
      warning: '#fcd34d',
      info: '#a78bfa',

      chartBackground: 'rgba(10, 10, 30, 0.9)',
      chartText: '#e0f2fe',
      chartGrid: 'rgba(125, 211, 252, 0.15)',
      chartBorder: 'rgba(35, 35, 70, 0.6)',
      candleUp: '#34d399',
      candleDown: '#fb7185',

      ema9: '#f0abfc',
      ema21: '#22d3ee',
      ema200: '#fcd34d',
      vwap: '#a78bfa',

      border: 'rgba(55, 55, 90, 0.6)',
      borderLight: 'rgba(75, 75, 120, 0.5)',
    },
  },

  // Cyberpunk
  cyberpunk: {
    id: 'cyberpunk',
    name: 'Cyberpunk',
    description: 'Neon pink & cyan dystopia',
    colors: {
      bgPrimary: '#0d0221',
      bgSecondary: '#1a0533',
      bgTertiary: '#2a0845',

      textPrimary: '#ff2a6d',
      textSecondary: '#d1f7ff',
      textMuted: '#05d9e8',

      accent: '#ff2a6d',
      accentHover: '#ff6b9d',

      success: '#05d9e8',
      error: '#ff2a6d',
      warning: '#f9c80e',
      info: '#05d9e8',

      chartBackground: '#0d0221',
      chartText: '#d1f7ff',
      chartGrid: 'rgba(5, 217, 232, 0.2)',
      chartBorder: '#2a0845',
      candleUp: '#05d9e8',
      candleDown: '#ff2a6d',

      ema9: '#ff2a6d',
      ema21: '#ff6b9d',
      ema200: '#f9c80e',
      vwap: '#05d9e8',

      border: '#2a0845',
      borderLight: '#3d0a5c',
    },
  },

  // Synthwave
  synthwave: {
    id: 'synthwave',
    name: 'Synthwave',
    description: '80s retro neon vibes',
    colors: {
      bgPrimary: '#241734',
      bgSecondary: '#2d1e44',
      bgTertiary: '#392654',

      textPrimary: '#f4eeff',
      textSecondary: '#e8ddff',
      textMuted: '#9d8ccc',

      accent: '#ff7edb',
      accentHover: '#ff9ee5',

      success: '#72f1b8',
      error: '#fe4450',
      warning: '#fede5d',
      info: '#36f9f6',

      chartBackground: '#241734',
      chartText: '#f4eeff',
      chartGrid: 'rgba(157, 140, 204, 0.2)',
      chartBorder: '#392654',
      candleUp: '#72f1b8',
      candleDown: '#fe4450',

      ema9: '#ff7edb',
      ema21: '#b893ff',
      ema200: '#fede5d',
      vwap: '#36f9f6',

      border: '#392654',
      borderLight: '#4a3365',
    },
  },

  // Matrix
  matrix: {
    id: 'matrix',
    name: 'Matrix',
    description: 'Green terminal hacker theme',
    colors: {
      bgPrimary: '#0a0a0a',
      bgSecondary: '#111111',
      bgTertiary: '#1a1a1a',

      textPrimary: '#00ff00',
      textSecondary: '#00cc00',
      textMuted: '#008800',

      accent: '#00ff00',
      accentHover: '#33ff33',

      success: '#00ff00',
      error: '#ff0000',
      warning: '#ffff00',
      info: '#00ffff',

      chartBackground: '#0a0a0a',
      chartText: '#00ff00',
      chartGrid: 'rgba(0, 255, 0, 0.1)',
      chartBorder: '#1a1a1a',
      candleUp: '#00ff00',
      candleDown: '#ff0000',

      ema9: '#00ffff',
      ema21: '#00ff00',
      ema200: '#ffff00',
      vwap: '#00cccc',

      border: '#1a1a1a',
      borderLight: '#2a2a2a',
    },
  },

  // Amoled Black
  amoled: {
    id: 'amoled',
    name: 'AMOLED Black',
    description: 'Pure black for OLED screens',
    colors: {
      bgPrimary: '#000000',
      bgSecondary: '#0a0a0a',
      bgTertiary: '#141414',

      textPrimary: '#ffffff',
      textSecondary: '#e0e0e0',
      textMuted: '#666666',

      accent: '#00d4ff',
      accentHover: '#33ddff',

      success: '#00e676',
      error: '#ff1744',
      warning: '#ffea00',
      info: '#00b0ff',

      chartBackground: '#000000',
      chartText: '#ffffff',
      chartGrid: 'rgba(255, 255, 255, 0.05)',
      chartBorder: '#141414',
      candleUp: '#00e676',
      candleDown: '#ff1744',

      ema9: '#e040fb',
      ema21: '#00d4ff',
      ema200: '#ffea00',
      vwap: '#00b0ff',

      border: '#1a1a1a',
      borderLight: '#2a2a2a',
    },
  },

  // Obsidian - Premium dark with subtle purple undertones
  obsidian: {
    id: 'obsidian',
    name: 'Obsidian',
    description: 'Premium dark with purple undertones',
    colors: {
      bgPrimary: '#0f0f14',
      bgSecondary: '#16161d',
      bgTertiary: '#1e1e28',

      textPrimary: '#e4e4ef',
      textSecondary: '#b8b8cc',
      textMuted: '#6b6b80',

      accent: '#9580ff',
      accentHover: '#aa99ff',

      success: '#8aff80',
      error: '#ff6e6e',
      warning: '#ffca80',
      info: '#80ffea',

      chartBackground: '#0f0f14',
      chartText: '#e4e4ef',
      chartGrid: 'rgba(149, 128, 255, 0.1)',
      chartBorder: '#1e1e28',
      candleUp: '#8aff80',
      candleDown: '#ff6e6e',

      ema9: '#ff80bf',
      ema21: '#9580ff',
      ema200: '#ffca80',
      vwap: '#80ffea',

      border: '#1e1e28',
      borderLight: '#2a2a38',
    },
  },

  // Vapor - Vaporwave aesthetic with pink and cyan
  vapor: {
    id: 'vapor',
    name: 'Vaporwave',
    description: 'Retro aesthetic pink & cyan',
    colors: {
      bgPrimary: '#16021f',
      bgSecondary: '#1f0630',
      bgTertiary: '#2a0b42',

      textPrimary: '#fff1fc',
      textSecondary: '#e8c8e8',
      textMuted: '#9966aa',

      accent: '#ff71ce',
      accentHover: '#ff9ddf',

      success: '#01cdfe',
      error: '#ff6b6b',
      warning: '#fffb96',
      info: '#05ffa1',

      chartBackground: '#16021f',
      chartText: '#fff1fc',
      chartGrid: 'rgba(255, 113, 206, 0.15)',
      chartBorder: '#2a0b42',
      candleUp: '#01cdfe',
      candleDown: '#ff6b6b',

      ema9: '#ff71ce',
      ema21: '#b967ff',
      ema200: '#fffb96',
      vwap: '#05ffa1',

      border: '#2a0b42',
      borderLight: '#3d1060',
    },
  },

  // Nightowl - VS Code Night Owl inspired
  nightowl: {
    id: 'nightowl',
    name: 'Night Owl',
    description: 'VS Code Night Owl inspired',
    colors: {
      bgPrimary: '#011627',
      bgSecondary: '#0b2942',
      bgTertiary: '#1d3b53',

      textPrimary: '#d6deeb',
      textSecondary: '#c5d4e0',
      textMuted: '#637777',

      accent: '#82aaff',
      accentHover: '#a1c0ff',

      success: '#22da6e',
      error: '#ef5350',
      warning: '#ffcb6b',
      info: '#7fdbca',

      chartBackground: '#011627',
      chartText: '#d6deeb',
      chartGrid: 'rgba(99, 119, 119, 0.25)',
      chartBorder: '#1d3b53',
      candleUp: '#22da6e',
      candleDown: '#ef5350',

      ema9: '#c792ea',
      ema21: '#82aaff',
      ema200: '#ffcb6b',
      vwap: '#7fdbca',

      border: '#1d3b53',
      borderLight: '#2d4f6c',
    },
  },

  // Palenight - Material Palenight
  palenight: {
    id: 'palenight',
    name: 'Palenight',
    description: 'Material Palenight theme',
    colors: {
      bgPrimary: '#292d3e',
      bgSecondary: '#32374d',
      bgTertiary: '#3c4260',

      textPrimary: '#a6accd',
      textSecondary: '#959dcb',
      textMuted: '#676e95',

      accent: '#c792ea',
      accentHover: '#d4a5f0',

      success: '#c3e88d',
      error: '#ff5370',
      warning: '#ffcb6b',
      info: '#89ddff',

      chartBackground: '#292d3e',
      chartText: '#a6accd',
      chartGrid: 'rgba(103, 110, 149, 0.3)',
      chartBorder: '#3c4260',
      candleUp: '#c3e88d',
      candleDown: '#ff5370',

      ema9: '#f78c6c',
      ema21: '#c792ea',
      ema200: '#ffcb6b',
      vwap: '#89ddff',

      border: '#3c4260',
      borderLight: '#4a5073',
    },
  },

  // Rosepine - Rose Pine theme
  rosepine: {
    id: 'rosepine',
    name: 'Rose Pine',
    description: 'All natural pine, faux fur',
    colors: {
      bgPrimary: '#191724',
      bgSecondary: '#1f1d2e',
      bgTertiary: '#26233a',

      textPrimary: '#e0def4',
      textSecondary: '#c4a7e7',
      textMuted: '#6e6a86',

      accent: '#c4a7e7',
      accentHover: '#d4bff0',

      success: '#9ccfd8',
      error: '#eb6f92',
      warning: '#f6c177',
      info: '#31748f',

      chartBackground: '#191724',
      chartText: '#e0def4',
      chartGrid: 'rgba(110, 106, 134, 0.25)',
      chartBorder: '#26233a',
      candleUp: '#9ccfd8',
      candleDown: '#eb6f92',

      ema9: '#ebbcba',
      ema21: '#c4a7e7',
      ema200: '#f6c177',
      vwap: '#31748f',

      border: '#26233a',
      borderLight: '#393552',
    },
  },

  // Everforest - Comfortable green theme
  everforest: {
    id: 'everforest',
    name: 'Everforest',
    description: 'Comfortable green forest theme',
    colors: {
      bgPrimary: '#2d353b',
      bgSecondary: '#343f44',
      bgTertiary: '#3d484d',

      textPrimary: '#d3c6aa',
      textSecondary: '#c5b799',
      textMuted: '#859289',

      accent: '#a7c080',
      accentHover: '#b8cf97',

      success: '#a7c080',
      error: '#e67e80',
      warning: '#dbbc7f',
      info: '#7fbbb3',

      chartBackground: '#2d353b',
      chartText: '#d3c6aa',
      chartGrid: 'rgba(133, 146, 137, 0.25)',
      chartBorder: '#3d484d',
      candleUp: '#a7c080',
      candleDown: '#e67e80',

      ema9: '#d699b6',
      ema21: '#7fbbb3',
      ema200: '#dbbc7f',
      vwap: '#83c092',

      border: '#3d484d',
      borderLight: '#4f5b58',
    },
  },

  // Kanagawa - Japanese wave theme
  kanagawa: {
    id: 'kanagawa',
    name: 'Kanagawa',
    description: 'Japanese ink wave inspired',
    colors: {
      bgPrimary: '#1f1f28',
      bgSecondary: '#2a2a37',
      bgTertiary: '#363646',

      textPrimary: '#dcd7ba',
      textSecondary: '#c8c093',
      textMuted: '#727169',

      accent: '#7e9cd8',
      accentHover: '#9cb4e8',

      success: '#98bb6c',
      error: '#c34043',
      warning: '#e6c384',
      info: '#7fb4ca',

      chartBackground: '#1f1f28',
      chartText: '#dcd7ba',
      chartGrid: 'rgba(114, 113, 105, 0.25)',
      chartBorder: '#363646',
      candleUp: '#98bb6c',
      candleDown: '#c34043',

      ema9: '#d27e99',
      ema21: '#7e9cd8',
      ema200: '#e6c384',
      vwap: '#7fb4ca',

      border: '#363646',
      borderLight: '#454559',
    },
  },

  // Horizon - Warm horizon theme
  horizon: {
    id: 'horizon',
    name: 'Horizon',
    description: 'Warm horizon sunset colors',
    colors: {
      bgPrimary: '#1c1e26',
      bgSecondary: '#232530',
      bgTertiary: '#2e303e',

      textPrimary: '#e9e9f4',
      textSecondary: '#d5d8da',
      textMuted: '#6c6f93',

      accent: '#e95678',
      accentHover: '#f06d85',

      success: '#27d796',
      error: '#e95678',
      warning: '#fab795',
      info: '#25b0bc',

      chartBackground: '#1c1e26',
      chartText: '#e9e9f4',
      chartGrid: 'rgba(108, 111, 147, 0.25)',
      chartBorder: '#2e303e',
      candleUp: '#27d796',
      candleDown: '#e95678',

      ema9: '#b877db',
      ema21: '#25b0bc',
      ema200: '#fab795',
      vwap: '#09f7a0',

      border: '#2e303e',
      borderLight: '#3d4052',
    },
  },

  // Moonlight - Soft moonlight theme
  moonlight: {
    id: 'moonlight',
    name: 'Moonlight',
    description: 'Soft moonlit night theme',
    colors: {
      bgPrimary: '#212337',
      bgSecondary: '#2a2e48',
      bgTertiary: '#343a58',

      textPrimary: '#c8d3f5',
      textSecondary: '#b4c2f0',
      textMuted: '#636da6',

      accent: '#82aaff',
      accentHover: '#9ec0ff',

      success: '#c3e88d',
      error: '#ff757f',
      warning: '#ffc777',
      info: '#86e1fc',

      chartBackground: '#212337',
      chartText: '#c8d3f5',
      chartGrid: 'rgba(99, 109, 166, 0.25)',
      chartBorder: '#343a58',
      candleUp: '#c3e88d',
      candleDown: '#ff757f',

      ema9: '#fca7ea',
      ema21: '#82aaff',
      ema200: '#ffc777',
      vwap: '#86e1fc',

      border: '#343a58',
      borderLight: '#444a6b',
    },
  },

  // Andromeda - Space theme with purples
  andromeda: {
    id: 'andromeda',
    name: 'Andromeda',
    description: 'Deep space purple galaxy',
    colors: {
      bgPrimary: '#23262e',
      bgSecondary: '#2b2e38',
      bgTertiary: '#343842',

      textPrimary: '#e5e5e5',
      textSecondary: '#d5d5d5',
      textMuted: '#676a77',

      accent: '#ee5d43',
      accentHover: '#f27c66',

      success: '#96e072',
      error: '#ee5d43',
      warning: '#ffe66d',
      info: '#00e8c6',

      chartBackground: '#23262e',
      chartText: '#e5e5e5',
      chartGrid: 'rgba(103, 106, 119, 0.25)',
      chartBorder: '#343842',
      candleUp: '#96e072',
      candleDown: '#ee5d43',

      ema9: '#c74ded',
      ema21: '#00e8c6',
      ema200: '#ffe66d',
      vwap: '#7cb7ff',

      border: '#343842',
      borderLight: '#44484f',
    },
  },

  // Shades of Purple - VS Code Shades of Purple
  shadesofpurple: {
    id: 'shadesofpurple',
    name: 'Shades of Purple',
    description: 'Vibrant purple coding theme',
    colors: {
      bgPrimary: '#2d2b55',
      bgSecondary: '#353361',
      bgTertiary: '#3e3c6e',

      textPrimary: '#fad000',
      textSecondary: '#e0d8ff',
      textMuted: '#a599e9',

      accent: '#ff9d00',
      accentHover: '#ffb340',

      success: '#a5ff90',
      error: '#ec3a37',
      warning: '#fad000',
      info: '#9effff',

      chartBackground: '#2d2b55',
      chartText: '#e0d8ff',
      chartGrid: 'rgba(165, 153, 233, 0.2)',
      chartBorder: '#3e3c6e',
      candleUp: '#a5ff90',
      candleDown: '#ec3a37',

      ema9: '#ff628c',
      ema21: '#ff9d00',
      ema200: '#fad000',
      vwap: '#9effff',

      border: '#3e3c6e',
      borderLight: '#504d7f',
    },
  },

  // Vitesse - Elegant minimalist
  vitesse: {
    id: 'vitesse',
    name: 'Vitesse',
    description: 'Elegant minimalist dark theme',
    colors: {
      bgPrimary: '#121212',
      bgSecondary: '#1a1a1a',
      bgTertiary: '#222222',

      textPrimary: '#dbd7ca',
      textSecondary: '#c9c5b8',
      textMuted: '#5c5c5c',

      accent: '#4d9375',
      accentHover: '#5da885',

      success: '#4d9375',
      error: '#cb7676',
      warning: '#e6cc77',
      info: '#6394bf',

      chartBackground: '#121212',
      chartText: '#dbd7ca',
      chartGrid: 'rgba(92, 92, 92, 0.25)',
      chartBorder: '#222222',
      candleUp: '#4d9375',
      candleDown: '#cb7676',

      ema9: '#d4976c',
      ema21: '#6394bf',
      ema200: '#e6cc77',
      vwap: '#80a665',

      border: '#222222',
      borderLight: '#333333',
    },
  },

  // Poimandres - Dreamy dark theme
  poimandres: {
    id: 'poimandres',
    name: 'Poimandres',
    description: 'Dreamy ethereal dark theme',
    colors: {
      bgPrimary: '#1b1e28',
      bgSecondary: '#232736',
      bgTertiary: '#2c3044',

      textPrimary: '#e4f0fb',
      textSecondary: '#a6accd',
      textMuted: '#506477',

      accent: '#add7ff',
      accentHover: '#c4e3ff',

      success: '#5de4c7',
      error: '#d0679d',
      warning: '#fffac2',
      info: '#89ddff',

      chartBackground: '#1b1e28',
      chartText: '#e4f0fb',
      chartGrid: 'rgba(80, 100, 119, 0.25)',
      chartBorder: '#2c3044',
      candleUp: '#5de4c7',
      candleDown: '#d0679d',

      ema9: '#fcc5e9',
      ema21: '#add7ff',
      ema200: '#fffac2',
      vwap: '#89ddff',

      border: '#2c3044',
      borderLight: '#3c4255',
    },
  },

  // Ayu Dark - Ayu theme dark variant
  ayudark: {
    id: 'ayudark',
    name: 'Ayu Dark',
    description: 'Warm Ayu dark variant',
    colors: {
      bgPrimary: '#0b0e14',
      bgSecondary: '#11151c',
      bgTertiary: '#1a1f29',

      textPrimary: '#bfbdb6',
      textSecondary: '#9da5b4',
      textMuted: '#565b66',

      accent: '#e6b450',
      accentHover: '#edc56b',

      success: '#7fd962',
      error: '#d95757',
      warning: '#e6b450',
      info: '#59c2ff',

      chartBackground: '#0b0e14',
      chartText: '#bfbdb6',
      chartGrid: 'rgba(86, 91, 102, 0.25)',
      chartBorder: '#1a1f29',
      candleUp: '#7fd962',
      candleDown: '#d95757',

      ema9: '#f07178',
      ema21: '#59c2ff',
      ema200: '#e6b450',
      vwap: '#73d0ff',

      border: '#1a1f29',
      borderLight: '#2a303c',
    },
  },

  // Bluloco Dark - Modern blue theme
  bluloco: {
    id: 'bluloco',
    name: 'Bluloco Dark',
    description: 'Modern blue professional theme',
    colors: {
      bgPrimary: '#282c34',
      bgSecondary: '#2f3441',
      bgTertiary: '#373d4a',

      textPrimary: '#abb2bf',
      textSecondary: '#9da5b4',
      textMuted: '#5c6370',

      accent: '#3691ff',
      accentHover: '#54a3ff',

      success: '#3fc56b',
      error: '#ff6480',
      warning: '#f9c859',
      info: '#10b1fe',

      chartBackground: '#282c34',
      chartText: '#abb2bf',
      chartGrid: 'rgba(92, 99, 112, 0.3)',
      chartBorder: '#373d4a',
      candleUp: '#3fc56b',
      candleDown: '#ff6480',

      ema9: '#ff78f8',
      ema21: '#10b1fe',
      ema200: '#f9c859',
      vwap: '#3691ff',

      border: '#373d4a',
      borderLight: '#474e5d',
    },
  },

  // Cobalt2 - Classic cobalt
  cobalt2: {
    id: 'cobalt2',
    name: 'Cobalt2',
    description: 'Classic cobalt blue theme',
    colors: {
      bgPrimary: '#122738',
      bgSecondary: '#193549',
      bgTertiary: '#234d6a',

      textPrimary: '#ffffff',
      textSecondary: '#e1efff',
      textMuted: '#6688aa',

      accent: '#ffc600',
      accentHover: '#ffd340',

      success: '#3ad900',
      error: '#ff628c',
      warning: '#ffc600',
      info: '#80fcff',

      chartBackground: '#122738',
      chartText: '#ffffff',
      chartGrid: 'rgba(102, 136, 170, 0.25)',
      chartBorder: '#234d6a',
      candleUp: '#3ad900',
      candleDown: '#ff628c',

      ema9: '#ff9d00',
      ema21: '#80fcff',
      ema200: '#ffc600',
      vwap: '#9effff',

      border: '#234d6a',
      borderLight: '#345f7e',
    },
  },

  // One Monokai - Hybrid theme
  onemonokai: {
    id: 'onemonokai',
    name: 'One Monokai',
    description: 'One Dark meets Monokai',
    colors: {
      bgPrimary: '#1e1f1c',
      bgSecondary: '#272822',
      bgTertiary: '#33342c',

      textPrimary: '#f8f8f2',
      textSecondary: '#e0e0dc',
      textMuted: '#75715e',

      accent: '#66d9ef',
      accentHover: '#85e3f4',

      success: '#a6e22e',
      error: '#f92672',
      warning: '#e6db74',
      info: '#66d9ef',

      chartBackground: '#1e1f1c',
      chartText: '#f8f8f2',
      chartGrid: 'rgba(117, 113, 94, 0.25)',
      chartBorder: '#33342c',
      candleUp: '#a6e22e',
      candleDown: '#f92672',

      ema9: '#fd971f',
      ema21: '#66d9ef',
      ema200: '#e6db74',
      vwap: '#ae81ff',

      border: '#33342c',
      borderLight: '#45463a',
    },
  },

  // Noctis - Serene dark theme
  noctis: {
    id: 'noctis',
    name: 'Noctis',
    description: 'Serene minimal dark theme',
    colors: {
      bgPrimary: '#0c0e15',
      bgSecondary: '#12161f',
      bgTertiary: '#1b202d',

      textPrimary: '#c5cdd3',
      textSecondary: '#a6b2c0',
      textMuted: '#5b6b7d',

      accent: '#49ace9',
      accentHover: '#67bcef',

      success: '#16b673',
      error: '#df769b',
      warning: '#d5971a',
      info: '#49ace9',

      chartBackground: '#0c0e15',
      chartText: '#c5cdd3',
      chartGrid: 'rgba(91, 107, 125, 0.25)',
      chartBorder: '#1b202d',
      candleUp: '#16b673',
      candleDown: '#df769b',

      ema9: '#7060eb',
      ema21: '#49ace9',
      ema200: '#d5971a',
      vwap: '#40b4c4',

      border: '#1b202d',
      borderLight: '#2a3140',
    },
  },

  // Darcula - JetBrains classic
  darcula: {
    id: 'darcula',
    name: 'Darcula',
    description: 'JetBrains IDE classic theme',
    colors: {
      bgPrimary: '#2b2b2b',
      bgSecondary: '#313335',
      bgTertiary: '#3c3f41',

      textPrimary: '#a9b7c6',
      textSecondary: '#9aa7b5',
      textMuted: '#606366',

      accent: '#cc7832',
      accentHover: '#d98c4d',

      success: '#6a8759',
      error: '#ff6b68',
      warning: '#bbb529',
      info: '#6897bb',

      chartBackground: '#2b2b2b',
      chartText: '#a9b7c6',
      chartGrid: 'rgba(96, 99, 102, 0.3)',
      chartBorder: '#3c3f41',
      candleUp: '#6a8759',
      candleDown: '#ff6b68',

      ema9: '#9876aa',
      ema21: '#6897bb',
      ema200: '#bbb529',
      vwap: '#cc7832',

      border: '#3c3f41',
      borderLight: '#515658',
    },
  },

  // Gotham - Dark superhero theme
  gotham: {
    id: 'gotham',
    name: 'Gotham',
    description: 'Dark city noir theme',
    colors: {
      bgPrimary: '#0a0f14',
      bgSecondary: '#11151c',
      bgTertiary: '#1a1f29',

      textPrimary: '#98d1ce',
      textSecondary: '#7ea7a5',
      textMuted: '#4a6a6d',

      accent: '#33859d',
      accentHover: '#4a9db5',

      success: '#2aa889',
      error: '#c23127',
      warning: '#d26937',
      info: '#195465',

      chartBackground: '#0a0f14',
      chartText: '#98d1ce',
      chartGrid: 'rgba(74, 106, 109, 0.25)',
      chartBorder: '#1a1f29',
      candleUp: '#2aa889',
      candleDown: '#c23127',

      ema9: '#888ca6',
      ema21: '#33859d',
      ema200: '#d26937',
      vwap: '#195465',

      border: '#1a1f29',
      borderLight: '#2a3540',
    },
  },

  // Laserwave - Retrofuturistic neon
  laserwave: {
    id: 'laserwave',
    name: 'Laserwave',
    description: 'Retrofuturistic neon glow',
    colors: {
      bgPrimary: '#27212e',
      bgSecondary: '#302a38',
      bgTertiary: '#3b3444',

      textPrimary: '#ffffff',
      textSecondary: '#e0d7f5',
      textMuted: '#7b6995',

      accent: '#eb64b9',
      accentHover: '#f07cc6',

      success: '#74dfc4',
      error: '#eb64b9',
      warning: '#ffe261',
      info: '#40b4c4',

      chartBackground: '#27212e',
      chartText: '#ffffff',
      chartGrid: 'rgba(123, 105, 149, 0.25)',
      chartBorder: '#3b3444',
      candleUp: '#74dfc4',
      candleDown: '#eb64b9',

      ema9: '#b381c5',
      ema21: '#40b4c4',
      ema200: '#ffe261',
      vwap: '#91daff',

      border: '#3b3444',
      borderLight: '#4d4556',
    },
  },

  // Solarized Dark - Classic readability
  solarizeddark: {
    id: 'solarizeddark',
    name: 'Solarized Dark',
    description: 'Classic high contrast theme',
    colors: {
      bgPrimary: '#002b36',
      bgSecondary: '#073642',
      bgTertiary: '#0d4555',

      textPrimary: '#839496',
      textSecondary: '#93a1a1',
      textMuted: '#586e75',

      accent: '#268bd2',
      accentHover: '#4aa3df',

      success: '#859900',
      error: '#dc322f',
      warning: '#b58900',
      info: '#2aa198',

      chartBackground: '#002b36',
      chartText: '#839496',
      chartGrid: 'rgba(88, 110, 117, 0.3)',
      chartBorder: '#0d4555',
      candleUp: '#859900',
      candleDown: '#dc322f',

      ema9: '#d33682',
      ema21: '#268bd2',
      ema200: '#b58900',
      vwap: '#2aa198',

      border: '#0d4555',
      borderLight: '#165a6d',
    },
  },

  // Outrun - 80s racing game aesthetic
  outrun: {
    id: 'outrun',
    name: 'Outrun',
    description: '80s arcade racing aesthetic',
    colors: {
      bgPrimary: '#0d0221',
      bgSecondary: '#150533',
      bgTertiary: '#200846',

      textPrimary: '#f5e6ff',
      textSecondary: '#e0c9ff',
      textMuted: '#8866aa',

      accent: '#ff00ff',
      accentHover: '#ff44ff',

      success: '#00ffaa',
      error: '#ff2975',
      warning: '#ffcc00',
      info: '#00ccff',

      chartBackground: '#0d0221',
      chartText: '#f5e6ff',
      chartGrid: 'rgba(136, 102, 170, 0.2)',
      chartBorder: '#200846',
      candleUp: '#00ffaa',
      candleDown: '#ff2975',

      ema9: '#ff00ff',
      ema21: '#00ccff',
      ema200: '#ffcc00',
      vwap: '#ff66b2',

      border: '#200846',
      borderLight: '#2d0c5a',
    },
  },

  // Noir - Film noir inspired
  noir: {
    id: 'noir',
    name: 'Noir',
    description: 'Film noir cinematic theme',
    colors: {
      bgPrimary: '#0a0a0a',
      bgSecondary: '#121212',
      bgTertiary: '#1a1a1a',

      textPrimary: '#d4d4d4',
      textSecondary: '#b0b0b0',
      textMuted: '#606060',

      accent: '#c9a227',
      accentHover: '#d9b847',

      success: '#4a9c6d',
      error: '#a83232',
      warning: '#c9a227',
      info: '#5a7fa8',

      chartBackground: '#0a0a0a',
      chartText: '#d4d4d4',
      chartGrid: 'rgba(96, 96, 96, 0.2)',
      chartBorder: '#1a1a1a',
      candleUp: '#4a9c6d',
      candleDown: '#a83232',

      ema9: '#8b6914',
      ema21: '#5a7fa8',
      ema200: '#c9a227',
      vwap: '#708090',

      border: '#1a1a1a',
      borderLight: '#2a2a2a',
    },
  },

  // Ember - Warm coal fire theme
  ember: {
    id: 'ember',
    name: 'Ember',
    description: 'Warm glowing coal fire',
    colors: {
      bgPrimary: '#171010',
      bgSecondary: '#201515',
      bgTertiary: '#2a1d1d',

      textPrimary: '#ffe6d5',
      textSecondary: '#e8c9b8',
      textMuted: '#8a6a5a',

      accent: '#ff6b35',
      accentHover: '#ff8655',

      success: '#8bc34a',
      error: '#e53935',
      warning: '#ffb74d',
      info: '#ff8a65',

      chartBackground: '#171010',
      chartText: '#ffe6d5',
      chartGrid: 'rgba(138, 106, 90, 0.2)',
      chartBorder: '#2a1d1d',
      candleUp: '#8bc34a',
      candleDown: '#e53935',

      ema9: '#ff6b35',
      ema21: '#ff8a65',
      ema200: '#ffb74d',
      vwap: '#ffa726',

      border: '#2a1d1d',
      borderLight: '#3a2828',
    },
  },

  // Synthwave Collection

  miamiVice: {
    id: 'miamiVice',
    name: 'Miami Vice',
    description: '80s Miami deco — coral & turquoise',
    colors: {
      bgPrimary: '#0a1628',
      bgSecondary: '#122240',
      bgTertiary: '#1a3058',

      textPrimary: '#fff5f0',
      textSecondary: '#ffd4c8',
      textMuted: '#8b7a8e',

      accent: '#ff5c8a',
      accentHover: '#ff7ea6',

      success: '#40e0d0',
      error: '#ff4757',
      warning: '#ffa502',
      info: '#70a1ff',

      chartBackground: '#0a1628',
      chartText: '#ffd4c8',
      chartGrid: 'rgba(255, 92, 138, 0.18)',
      chartBorder: '#1a3058',

      candleUp: '#40e0d0',
      candleDown: '#ff4757',

      ema9: '#ff5c8a',
      ema21: '#40e0d0',
      ema200: '#ffa502',
      vwap: '#70a1ff',

      border: '#1a3058',
      borderLight: '#254070',
    },
  },

  tronLegacy: {
    id: 'tronLegacy',
    name: 'Tron Legacy',
    description: 'Digital grid — ice blue & orange',
    colors: {
      bgPrimary: '#05080f',
      bgSecondary: '#0c1220',
      bgTertiary: '#141e33',

      textPrimary: '#dfeeff',
      textSecondary: '#8cb4d8',
      textMuted: '#4a6a8a',

      accent: '#6fc3df',
      accentHover: '#8dd4eb',

      success: '#6fc3df',
      error: '#df740c',
      warning: '#f5a623',
      info: '#18b4d0',

      chartBackground: '#05080f',
      chartText: '#8cb4d8',
      chartGrid: 'rgba(111, 195, 223, 0.15)',
      chartBorder: '#141e33',

      candleUp: '#6fc3df',
      candleDown: '#df740c',

      ema9: '#6fc3df',
      ema21: '#df740c',
      ema200: '#f5a623',
      vwap: '#18b4d0',

      border: '#141e33',
      borderLight: '#1f2e48',
    },
  },

  bloodDragon: {
    id: 'bloodDragon',
    name: 'Blood Dragon',
    description: 'Neon blood red & acid green',
    colors: {
      bgPrimary: '#1a0a0e',
      bgSecondary: '#2a1018',
      bgTertiary: '#3a1a24',

      textPrimary: '#ffe0e6',
      textSecondary: '#ffb0c0',
      textMuted: '#995566',

      accent: '#ff1744',
      accentHover: '#ff4569',

      success: '#00e676',
      error: '#ff1744',
      warning: '#ffea00',
      info: '#00e5ff',

      chartBackground: '#1a0a0e',
      chartText: '#ffb0c0',
      chartGrid: 'rgba(255, 23, 68, 0.18)',
      chartBorder: '#3a1a24',

      candleUp: '#00e676',
      candleDown: '#ff1744',

      ema9: '#ff1744',
      ema21: '#00e5ff',
      ema200: '#ffea00',
      vwap: '#00e676',

      border: '#3a1a24',
      borderLight: '#4d2430',
    },
  },

  midnightDrive: {
    id: 'midnightDrive',
    name: 'Midnight Drive',
    description: 'Chrome gold on charcoal — nocturnal cruise',
    colors: {
      bgPrimary: '#0e1118',
      bgSecondary: '#161b26',
      bgTertiary: '#1f2636',

      textPrimary: '#fffae6',
      textSecondary: '#d4c49a',
      textMuted: '#7a7060',

      accent: '#e8a838',
      accentHover: '#f0be5a',

      success: '#5cb85c',
      error: '#d9534f',
      warning: '#e8a838',
      info: '#5bc0de',

      chartBackground: '#0e1118',
      chartText: '#d4c49a',
      chartGrid: 'rgba(232, 168, 56, 0.18)',
      chartBorder: '#1f2636',

      candleUp: '#5cb85c',
      candleDown: '#d9534f',

      ema9: '#e8a838',
      ema21: '#e85d75',
      ema200: '#f0be5a',
      vwap: '#5bc0de',

      border: '#1f2636',
      borderLight: '#2a3348',
    },
  },

  sovietwave: {
    id: 'sovietwave',
    name: 'Sovietwave',
    description: 'Muted rose & teal — Soviet nostalgia',
    colors: {
      bgPrimary: '#141410',
      bgSecondary: '#1e1e18',
      bgTertiary: '#2a2a22',

      textPrimary: '#e8dfd0',
      textSecondary: '#c4b8a0',
      textMuted: '#7a7260',

      accent: '#c27070',
      accentHover: '#d48888',

      success: '#5f9ea0',
      error: '#c27070',
      warning: '#d4a848',
      info: '#5f9ea0',

      chartBackground: '#141410',
      chartText: '#c4b8a0',
      chartGrid: 'rgba(194, 112, 112, 0.18)',
      chartBorder: '#2a2a22',

      candleUp: '#5f9ea0',
      candleDown: '#c27070',

      ema9: '#c27070',
      ema21: '#5f9ea0',
      ema200: '#d4a848',
      vwap: '#8fbc8f',

      border: '#2a2a22',
      borderLight: '#363630',
    },
  },
};

export const defaultTheme = themes.midnight;

// LocalStorage key for fast initial theme load
const THEME_CACHE_KEY = 'utt_theme_cache';

// Get theme - uses localStorage cache for fast initial load, falls back to server prefs
export const getStoredTheme = (): Theme => {
  // First, try localStorage cache for instant load (before server fetch completes)
  try {
    const cachedThemeId = localStorage.getItem(THEME_CACHE_KEY);
    if (cachedThemeId && themes[cachedThemeId]) {
      return themes[cachedThemeId];
    }
  } catch (e) {
    // localStorage might not be available
  }

  // Fall back to server preferences (might be empty on initial load)
  const prefs = getPreferences();
  const storedThemeId = prefs.theme;
  if (storedThemeId && themes[storedThemeId]) {
    return themes[storedThemeId];
  }
  return defaultTheme;
};

// Save theme to both localStorage (for fast load) and server (for sync)
export const saveTheme = (themeId: string): void => {
  // Cache in localStorage for fast initial load on refresh
  try {
    localStorage.setItem(THEME_CACHE_KEY, themeId);
  } catch (e) {
    // localStorage might not be available
  }
  // Persist to server for cross-device sync
  updatePreferenceDirect('theme', themeId);
};

/**
 * Convert hex color to RGB string for use in rgba()
 * @param hex - Hex color string (e.g., '#0a0e27' or '0a0e27')
 * @returns RGB string (e.g., '10, 14, 39')
 */
export const hexToRgb = (hex: string): string => {
  // Handle rgba() colors - extract just the RGB part
  if (hex.startsWith('rgba(')) {
    const match = hex.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (match) {
      return `${match[1]}, ${match[2]}, ${match[3]}`;
    }
  }

  // Handle rgb() colors
  if (hex.startsWith('rgb(')) {
    const match = hex.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (match) {
      return `${match[1]}, ${match[2]}, ${match[3]}`;
    }
  }

  // Remove # if present
  const cleanHex = hex.replace('#', '');

  // Parse hex to RGB
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  // Handle NaN (invalid hex)
  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return '0, 0, 0';
  }

  return `${r}, ${g}, ${b}`;
};

/**
 * Get RGB variants for a theme's colors
 * Used to enable rgba() with theme colors in CSS
 */
export const getThemeRgbVariants = (theme: Theme): Record<string, string> => {
  const rgbVariants: Record<string, string> = {};

  // Only convert main colors that need rgba() usage
  const colorsNeedingRgb = [
    'bgPrimary', 'bgSecondary', 'bgTertiary',
    'accent', 'accentHover',
    'success', 'error', 'warning', 'info',
    'textPrimary', 'textSecondary', 'textMuted',
    'border', 'borderLight'
  ];

  colorsNeedingRgb.forEach(colorKey => {
    const color = theme.colors[colorKey as keyof typeof theme.colors];
    if (color) {
      rgbVariants[`${colorKey}Rgb`] = hexToRgb(color);
    }
  });

  return rgbVariants;
};
